import {
  BUILDINGS,
  BuildingType,
  type BuildingTypeId,
  type BuildingView,
  dollars,
  footprint,
} from './buildings';

/*
 * The airport city: cafés, restaurants, a shopping mall, a fuel station, office blocks and parks
 * on the landside, which the player lays out like a town. Every hour customers come from three
 * places: the airport's passengers (and those who see them off or meet them; yesterday's count
 * spread over the day), people from the city around (more for a well-rated airport and a bigger
 * city), and the offices' workers (coffee in the morning and afternoon, lunch at noon). Each
 * kind of business takes its share of each, split among the businesses of that kind by their
 * size, each up to its capacity; the airport takes its cut of every sale. A business must be on
 * a road (customers come by car); passengers reach those near a terminal, a mall draws them from
 * further. The layout pays: businesses near each other draw more (a high street), parks make
 * their neighbourhood pleasant, and a mall brings people to the cafés and restaurants around it.
 * The offices pay rent, more the better the airport is rated.
 *
 * Pure: the simulation runs it every hour (operations.ts), the UI shows the same figures.
 */

/** The airport city's businesses (a park draws people, it sells nothing). */
export const CITY_TYPES: ReadonlySet<number> = new Set<number>([
  BuildingType.Cafe,
  BuildingType.Restaurant,
  BuildingType.Mall,
  BuildingType.FuelStation,
  BuildingType.OfficePark,
  BuildingType.CityPark,
]);

/** What a kind of business sells to, per level: customers an hour it can serve, and the cut. */
interface Trade {
  /** Customers an hour, by level. */
  capacity: readonly number[];
  /** What the airport takes per customer (cents). */
  take: number;
  /** Per mille of the hour's passengers, people from the city and passing cars who come. */
  passengers: number;
  locals: number;
  cars: number;
  /** Per mille of office workers who come in the morning/afternoon break, and at lunch. */
  coffee: number;
  lunch: number;
  /** Tiles (across the ground) from a terminal that passengers walk or drive to it. */
  reach: number;
}

export const TRADES: Readonly<Partial<Record<BuildingTypeId, Trade>>> = {
  [BuildingType.Cafe]: {
    capacity: [0, 30, 60],
    take: dollars(3),
    passengers: 80,
    locals: 140,
    cars: 0,
    coffee: 300,
    lunch: 200,
    reach: 40,
  },
  [BuildingType.Restaurant]: {
    capacity: [0, 40, 80],
    take: dollars(8),
    passengers: 45,
    locals: 110,
    cars: 0,
    coffee: 0,
    lunch: 700,
    reach: 40,
  },
  [BuildingType.Mall]: {
    capacity: [0, 300, 550, 900],
    take: dollars(9),
    passengers: 150,
    locals: 600,
    cars: 0,
    coffee: 0,
    lunch: 100,
    reach: 80,
  },
  [BuildingType.FuelStation]: {
    capacity: [0, 40, 80],
    take: dollars(6),
    passengers: 0,
    locals: 60,
    cars: 140,
    coffee: 0,
    lunch: 0,
    reach: 0,
  },
};

/** Office rent a day at full occupancy (cents), by level; and workers per level. */
export const OFFICE_RENT: readonly number[] = [
  0,
  dollars(12_000),
  dollars(22_000),
  dollars(34_000),
];
export const OFFICE_WORKERS = 120;

/** Share of a day's people (per mille) about in each hour: quiet at night, busy by day. */
export const HOUR_SHARE: readonly number[] = [
  8, 5, 4, 4, 8, 20, 40, 55, 60, 58, 56, 58, 62, 60, 56, 55, 58, 62, 64, 60, 52, 42, 30, 18,
];

/** People from the city a day: a base, more for a well-rated airport and per business. */
export const LOCALS_BASE = 400;
export const LOCALS_PER_RATING = 600;
export const LOCALS_PER_BUSINESS = 120;
/** Passengers to a car on the way in (tenths; operations.ts PAX_PER_CAR_TENTHS). */
const PAX_PER_CAR_TENTHS = 18;

/** A park within PARK_REACH lifts a business by PARK_BONUS a level, at most PARK_MAX. */
export const PARK_REACH = 20;
export const PARK_BONUS = 10;
export const PARK_MAX = 30;
/** Each business within STREET_REACH lifts another by STREET_BONUS, at most STREET_MAX. */
export const STREET_REACH = 16;
export const STREET_BONUS = 5;
export const STREET_MAX = 25;
/** A café or restaurant within MALL_REACH of a mall draws MALL_BONUS more. */
export const MALL_REACH = 24;
export const MALL_BONUS = 15;
/** Comfort passengers give an airport with a café, restaurant or mall near a terminal. */
export const CITY_COMFORT = 40;

export interface CityInput {
  readonly buildings: readonly BuildingView[];
  /** Whether a building has a road to the motorway (customers come by car). */
  readonly connected: (b: BuildingView) => boolean;
  /** Airport rating 0–1000. */
  readonly rating: number;
  /** Passengers the airport handled yesterday. */
  readonly passengers: number;
  /** Hour of day, 0–23. */
  readonly hour: number;
}

export interface CityBusiness {
  readonly id: number;
  /** Customers this hour, what it could serve, and the airport's take (cents). */
  readonly customers: number;
  readonly capacity: number;
  readonly revenue: number;
  /** Percent it draws over a lone business: 100 plus the bonuses. */
  readonly attraction: number;
  /** Bonus points from parks, the street around it and a mall nearby. */
  readonly park: number;
  readonly street: number;
  readonly mall: number;
  /** Near enough a terminal for passengers to come. */
  readonly nearTerminal: boolean;
}

export interface CityHour {
  readonly businesses: CityBusiness[];
  readonly customers: number;
  /** Sales (the airport's take) and office rent this hour (cents). */
  readonly sales: number;
  readonly rent: number;
}

/** The middle of a building's footprint (tiles). */
function middle(b: BuildingView): [number, number] {
  const { w, h } = footprint(BUILDINGS[b.type], b.rot);
  return [b.x + Math.floor(w / 2), b.y + Math.floor(h / 2)];
}

function apart(a: [number, number], b: [number, number]): number {
  return Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]);
}

/** Office rent per hour at an occupancy (percent) set by the rating: half at worst, all at 800+. */
export function officeOccupancy(rating: number): number {
  return Math.min(100, 50 + Math.floor((Math.max(0, rating) * 50) / 800));
}

/** One hour of the airport city. */
export function cityHour(input: CityInput): CityHour {
  const { buildings, hour } = input;
  const open = buildings.filter(
    (b) => CITY_TYPES.has(b.type) && b.level >= 1 && b.buildRemaining === 0 && input.connected(b),
  );
  const terminals = buildings
    .filter((b) => b.type === BuildingType.Terminal && b.level >= 1 && input.connected(b))
    .map(middle);
  const share = HOUR_SHARE[hour] ?? 0;
  const shops = open.filter((b) => TRADES[b.type] !== undefined);
  // The hour's people.
  const passengers = Math.floor((Math.max(0, input.passengers) * share) / 1000);
  const cars = Math.floor((passengers * 10) / PAX_PER_CAR_TENTHS);
  const localsDay =
    LOCALS_BASE +
    Math.floor((Math.max(0, input.rating) * LOCALS_PER_RATING) / 1000) +
    shops.length * LOCALS_PER_BUSINESS;
  const locals = Math.floor((localsDay * share) / 1000);
  let workers = 0;
  for (const b of open) if (b.type === BuildingType.OfficePark) workers += b.level * OFFICE_WORKERS;
  const coffee = hour === 9 || hour === 16 ? workers : 0;
  const lunch =
    hour === 12 ? Math.floor(workers / 2) : hour === 13 ? workers - Math.floor(workers / 2) : 0;

  // How much each business draws: parks, the street around it, a mall nearby.
  const at = new Map(open.map((b) => [b.id, middle(b)]));
  const pull = new Map<number, { park: number; street: number; mall: number }>();
  for (const b of shops) {
    const here = at.get(b.id) as [number, number];
    let park = 0;
    let street = 0;
    let mall = 0;
    for (const o of open) {
      if (o.id === b.id) continue;
      const d = apart(here, at.get(o.id) as [number, number]);
      if (o.type === BuildingType.CityPark && d <= PARK_REACH) park += PARK_BONUS * o.level;
      else if (o.type !== BuildingType.CityPark && d <= STREET_REACH) street += STREET_BONUS;
      if (
        o.type === BuildingType.Mall &&
        (b.type === BuildingType.Cafe || b.type === BuildingType.Restaurant) &&
        d <= MALL_REACH
      ) {
        mall = MALL_BONUS;
      }
    }
    pull.set(b.id, { park: Math.min(PARK_MAX, park), street: Math.min(STREET_MAX, street), mall });
  }

  const businesses: CityBusiness[] = [];
  let customersAll = 0;
  let sales = 0;
  for (const type of [
    BuildingType.Cafe,
    BuildingType.Restaurant,
    BuildingType.Mall,
    BuildingType.FuelStation,
  ]) {
    const trade = TRADES[type] as Trade;
    const kind = shops.filter((b) => b.type === type);
    if (kind.length === 0) continue;
    const near = (b: BuildingView) => {
      const here = at.get(b.id) as [number, number];
      return trade.reach > 0 && terminals.some((t) => apart(here, t) <= trade.reach);
    };
    const cap = (b: BuildingView) => trade.capacity[b.level] ?? 0;
    // Passengers go to those near a terminal; everyone else to any of them, by size.
    const reached = kind.filter(near);
    const passengerDemand = Math.floor((passengers * trade.passengers) / 1000);
    const otherDemand =
      Math.floor((locals * trade.locals) / 1000) +
      Math.floor((cars * trade.cars) / 1000) +
      Math.floor((coffee * trade.coffee) / 1000) +
      Math.floor((lunch * trade.lunch) / 1000);
    const allCap = kind.reduce((n, b) => n + cap(b), 0);
    const nearCap = reached.reduce((n, b) => n + cap(b), 0);
    for (const b of kind) {
      const p = pull.get(b.id) ?? { park: 0, street: 0, mall: 0 };
      const attraction = 100 + p.park + p.street + p.mall;
      const isNear = reached.includes(b);
      const base =
        (allCap > 0 ? Math.floor((otherDemand * cap(b)) / allCap) : 0) +
        (isNear && nearCap > 0 ? Math.floor((passengerDemand * cap(b)) / nearCap) : 0);
      const customers = Math.min(cap(b), Math.floor((base * attraction) / 100));
      const revenue = customers * trade.take;
      customersAll += customers;
      sales += revenue;
      businesses.push({
        id: b.id,
        customers,
        capacity: cap(b),
        revenue,
        attraction,
        park: p.park,
        street: p.street,
        mall: p.mall,
        nearTerminal: isNear,
      });
    }
  }
  // Office rent, by the hour.
  let rent = 0;
  const occupancy = officeOccupancy(input.rating);
  for (const b of open) {
    if (b.type !== BuildingType.OfficePark) continue;
    const r = Math.floor(((OFFICE_RENT[b.level] ?? 0) * occupancy) / 100 / 24);
    rent += r;
    businesses.push({
      id: b.id,
      customers: b.level * OFFICE_WORKERS,
      capacity: b.level * OFFICE_WORKERS,
      revenue: r,
      attraction: 100,
      park: 0,
      street: 0,
      mall: 0,
      nearTerminal: false,
    });
  }
  return { businesses, customers: customersAll, sales, rent };
}

/** Whether passengers find somewhere to eat or shop near a terminal (comfort). */
export function cityComfort(input: Omit<CityInput, 'hour' | 'rating' | 'passengers'>): boolean {
  const terminals = input.buildings
    .filter((b) => b.type === BuildingType.Terminal && b.level >= 1 && input.connected(b))
    .map(middle);
  return input.buildings.some((b) => {
    if (b.level < 1 || b.buildRemaining > 0 || !input.connected(b)) return false;
    const trade =
      b.type === BuildingType.Cafe ||
      b.type === BuildingType.Restaurant ||
      b.type === BuildingType.Mall
        ? TRADES[b.type]
        : undefined;
    if (!trade) return false;
    const here = middle(b);
    return terminals.some((t) => apart(here, t) <= trade.reach);
  });
}
